'use client'

import { useState } from 'react'
import { useTranslations } from 'next-intl'
import { useServices } from '@/lib/hooks/useServices'
import { useUnifiedCart } from '@/lib/unified-cart'
import ServiceQuickAddModal from './ServiceQuickAddModal'
import type { Activity } from '@/lib/hooks/useActivities'
import type { Adventure } from '@/lib/data'
import styles from './ServiceRecommendation.module.css'

interface ServiceRecommendationProps {
  context: 'lodge' | 'activity' | 'package' | 'checkout'
  prefillDates?: { checkIn?: string; checkOut?: string }
  prefillGuests?: number
  onAdded?: () => void
}

function toAdventureShim(activity: Activity): Adventure {
  const name = activity.name
  return {
    id: activity.id,
    title: name,
    shortTitle: name,
    description: activity.description || activity.shortDescription || '',
    heroImage: activity.heroImage || '/images/placeholder-activity.webp',
    link: `/booking/activities/${activity.slug || activity.id}`,
    subtitle: activity.subtitle || activity.shortDescription || '',
    location: activity.location?.name || '',
    bookingMode: 'direct' as const,
  }
}

export default function ServiceRecommendation({
  context: _context,
  prefillDates,
  prefillGuests,
  onAdded,
}: ServiceRecommendationProps) {
  const t = useTranslations('serviceRecommendation')
  const { services, loading } = useServices()
  const { serviceItems } = useUnifiedCart()
  const [modalOpen, setModalOpen] = useState(false)

  // Feature flag — services recommendation. Re-enabled 2026-05-19.
  const SERVICES_RECOMMENDATION_ENABLED = true
  if (!SERVICES_RECOMMENDATION_ENABLED) return null

  if (loading) return null
  if (services.length === 0) return null
  if (serviceItems.length > 0) return null

  const sorted = [...services].sort(
    (a, b) => (a.basePrice?.amount ?? 0) - (b.basePrice?.amount ?? 0)
  )
  const service = sorted[0]
  const adventure = toAdventureShim(service)
  const price = service.basePrice?.amount ?? 0

  const handleAdded = () => {
    setModalOpen(false)
    onAdded?.()
  }

  return (
    <>
      <div className={styles.card}>
        <div className={styles.icon}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="3" width="15" height="13" rx="1" />
            <path d="M16 8h4l3 5v3h-7V8z" />
            <circle cx="5.5" cy="18.5" r="2.5" />
            <circle cx="18.5" cy="18.5" r="2.5" />
          </svg>
        </div>
        <div className={styles.content}>
          <p className={styles.heading}>{t('heading')}</p>
          <p className={styles.subheading}>{t('subheading', { price })}</p>
        </div>
        <button
          type="button"
          className={styles.cta}
          onClick={() => setModalOpen(true)}
        >
          {t('cta')}
        </button>
      </div>

      {modalOpen && (
        <ServiceQuickAddModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          activity={adventure}
          firestoreActivity={service as never}
          prefillDates={prefillDates}
          prefillGuests={prefillGuests}
          onAdded={handleAdded}
        />
      )}
    </>
  )
}
